import * as PropTypes from "prop-types";
import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {SYSTEMADMIN} from "../../consts/funktionaleRollen";
import http, {handleApiException, isApiCancel, useHttp} from "../../services/HttpService";
import {logger} from "../../services/LogService";
import {createCalculatedColumn, createColumn, createComponentColumn} from "../../utils/ColumnUtils";
import {useForm} from "../../utils/FormUtils";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import EAButton from "../EAButton";
import EAButtonBar from "../EAButtonBar";
import EACombobox from "../EACombobox";
import EADialog from "../EADialog";
import EATable from "../EATable";
import EATextField from "../EATextField";
import EATableActionButton from "../tableComponents/EATableActionButton";
import {useConfirmation} from "../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../ConfirmationDialog/confirmationDialogOptions";
import LoadingPlaceholder from "../LoadingPlaceholder";
import {createWertelisteSchema} from "../../initialObjects/WertelistenObjects";

function TableDialog({ open, data, referenzLabel, referenz, referenzListe, onClickOk, onClickCancel, saving, referenzIsMultiselection, additionalColumns }) {
    const { values, formFactory, validate } = useForm({
        initialValues: data,
        schema: createWertelisteSchema(referenz, referenzIsMultiselection, additionalColumns),
    });
    const [referenzOptions] = useHttp(referenzListe, { manual: !referenzListe });

    return (
        <EADialog
            open={open}
            titleChildren={"Wert " + (data.id ? "bearbeiten" : "erstellen")}
            content={
                <div style={{ width: "800px" }}>
                    <div className="gridl" style={{ gridTemplateColumns: "1fr" }}>
                        {saving && <LoadingPlaceholder />}
                        {!saving && formFactory.renderInput("value", "value", "Wert")}
                        {/* Schleife für zusätzliche Input-Felder */}
                        {!saving &&
                            additionalColumns &&
                            additionalColumns.map((column) => (
                                <React.Fragment key={column.id}>
                                    {!saving && !column.isBool && formFactory.renderInput(column.id, column.id, column.label)}
                                    {!saving && column.isBool && formFactory.renderCheckbox(column.id, column.id, column.label)}
                                </React.Fragment>
                            ))}
                        {!saving &&
                            referenz &&
                            referenzLabel &&
                            formFactory.renderAutocompleteWerteliste(
                                referenz,
                                "referenz",
                                referenzLabel,
                                referenzOptions,
                                !!referenzIsMultiselection
                            )}
                    </div>
                </div>
            }
            buttons={
                !saving && (
                    <EAButtonBar>
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={onClickCancel} />
                        <EAButton
                            id={"btnOk"}
                            data-cy={"btnOk"}
                            color="primary"
                            label={"Übernehmen"}
                            onClick={() => {
                                if (validate()) return;
                                onClickOk(values);
                            }}
                        />
                    </EAButtonBar>
                )
            }
        />
    );
}


TableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    data: PropTypes.any.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

const wertelistenOptions = [
    {id: "/anrede/", value: "Anrede"},
    {id: "/abwasserart/", value: "Abwasserart"},
    {id: "/anforderung-randbedingung/", value: "Anforderung Randbedingung"},
    {id: "/anforderung-vorbehandlung/", value: "Anforderung Vorbehandlung"},
    {id: "/anforderung-wert/", value: "Anforderung Wert"},
    {id: "/auflagenart/", value: "Auflagenart"},
    {id: "/bauwerk-anlageteil-kategorie/", value: "Anlagenteile Kategorie"},
    {id: "/bauwerk-bezeichner/", value: "Anlagenteile Bezeichner"},
    {id: "/bauwerk-einheit/", value: "Anlagenteile Einheit"},
    {id: "/kanalsystem/", value: "Kanalsystem"},
    {id: "/kuehlart/", value: "Kühlart"},
    {id: "/kanal-materialart/", value: "Kanal Materialart"},
    {id: "/messstelle-abwasserart/", value: "Messstelle Abwasserart"},
    {id: "/planung-massnahme/", value: "Planung Maßnahme"},
    {id: "/planung-status/", value: "Planung Status"},
    {id: "/produktionsart/", value: "Produktionsart"},
    {id: "/produktbezeichnung/", value: "Produktbezeichnung"},
    {id: "/haupttaetigkeit/", value: "NACE-Tätigkeit"},
    {id: "/nace-code/", value: "NACE-Code"},
    {id: "/taetigkeit/", value: "Nr. IE-RL"},
    {id: "/bimschv/", value: "Nr. 4.BImSchV"},
    {id: "/bvt-bref/", value: "BvT / BREF"},
    {id: "/prtr-vo/", value: "Nr. PRTR-VO"},
    {id: "/titel/", value: "Titel"},
    {id: "/produktionsverfahren/", value: "Produktionsverfahren"},
    {id: "/zustaendigkeit-person/", value: "Zuständigkeit Person"},
    {id: "/bauwerk-anlageteil-bezeichnung/", value: "Anlagenteile Bezeichnung"},
    {id: "/anlagensystem/", value: "Anlagensystem"},
    {id: "/biologische_behandlung/", value: "Biologische Behandlung"},
    {id: "/schlammbehandlung/", value: "Schlammbehandlung"},
    {id: "/vorbehandlung/", value: "Vorbehandlung"},
    {id: "/chemische-behandlung/", value: "Chemische Behandlung"},
    {id: "/nachbehandlung/", value: "Nachbehandlung/Abtrennung"},
    {id: "/as-parameter/", value: "AS-Parameter"},
    {id: "/as-einheit/", value: "AS-Einheit"},
    {
        id: "/entlastungstyp/",
        value: "Anlagensystem (Misch-Niederschlagswasser)",
        referenzIsMultiselection: true,
        referenzBezeichnung: "Entlastungssysteme",
        referenz: "entlastungssysteme",
        referenzAbfrage: "/entlastungssystem/"
    },
    {id: "/entlastungssystem/", value: "Verfahren (Misch-Niederschlagswasser)"},
    {id: "/suevoaAnlageNr/", value: "SÜVOA Anlage Nr."},
    {
        id: "/selbstueberwachungVorlage/",
        value: "Selbstüberwachung Vorlage",
        additionalColumns: [
            {id: "datumsformat", label: "Datumsformat"},
            {id: "maxZeilenAnzahl", label: "Max Zeilen Anzahl"},
            {id: "uhrzeit", label: "Uhrzeit", isBool: true},
            {id: "wetterschluessel", label: "Wetterschlüssel", isBool: true},
            {id: "schwelle", label: "Schwelle", isBool: true},
            {id: "wert", label: "Wert", isBool: true},
            {id: "messwertAnzahl", label: "Messwert Anzahl", isBool: true},
            {id: "messwertAnzahlGreater12", label: "Messwert Anzahl > 12", isBool: true},
            {id: "messwertMaxWert", label: "Messwert Max Wert", isBool: true},
            {id: "messwertMaxQ", label: "Messwert Max Q", isBool: true},
            {id: "konzentration", label: "Konzentration", isBool: true},
            {id: "abfluss", label: "Wassermenge", isBool: true},
        ]
    },
];

const buildColumns = (isDisabled, selectedWerteListe, onEdit, onDelete) => {
    const columns = [
        createComponentColumn(
            "actions",
            (row, index) => {
                if (isDisabled) return <div/>;
                return (
                    <div className="gridl" style={{gridTemplateColumns: "repeat(2,min-content)", marginLeft: "8px"}}>
                        <EATableActionButton id={"editWert-" + index} iconName="ico-edit">
                                        <span
                                            onClick={() => onEdit(row.id)}
                                            className={"w-100"}
                                            style={{
                                                height: "100%",
                                                display: "block",
                                            }}
                                        />
                        </EATableActionButton>
                        <EATableActionButton id={"deleteWert-" + index} iconName="ico-delete">
                                        <span
                                            onClick={() => onDelete(row.id)}
                                            className={"w-100"}
                                            style={{
                                                height: "100%",
                                                display: "block",
                                            }}
                                        />
                        </EATableActionButton>
                    </div>
                );
            },
            "",
            null,
            {width: "50px", padding: "0"}
        ),
        createColumn("id", " ID"),
        createColumn("value", " Wert")
    ];

    if (selectedWerteListe && selectedWerteListe.additionalColumns) {
        for (const columnData of selectedWerteListe.additionalColumns) {
            let column;
            if (columnData.isBool) {
                column = createComponentColumn(
                    columnData.id,
                    (row) => (row[columnData.id] ? "x" : ""),
                    columnData.label
                )
            }
            else {
               column  = createColumn(columnData.id, columnData.label);
            }
            columns.push(column);
        }
    }

    if (selectedWerteListe && selectedWerteListe.referenz) {
        let referenzColumn;
        if (selectedWerteListe.referenzIsMultiselection) {
            const referenzColumnValueProvider = (row) => {
                let items = row[selectedWerteListe.referenz]?.map(item => item.value).join(", ");
                if (!items) {
                    items = "";
                }
                return items;
            };
            referenzColumn = createCalculatedColumn("referenz-column-" + selectedWerteListe.referenz,
                referenzColumnValueProvider,
                selectedWerteListe.referenzBezeichnung);
        } else {
            referenzColumn = createColumn(selectedWerteListe.referenz + ".value", selectedWerteListe.referenzBezeichnung)
        }

        columns.push(referenzColumn);
    }
    return columns;
};

const WertelistenComponent = () => {
    const confirm = useConfirmation();
    const [saving, setSaving] = useState(false);
    const [selectedWerteListe, setSelectedWerteListe] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");
    const [dlgOpen, setDlgOpen] = useState(false);
    const [tableIndex, setTableIndex] = useState(-1);
    const [{data, loading}, refetch] = useHttp("", {manual: true});

    useEffect(() => {
        if (selectedWerteListe) {
            refetch(selectedWerteListe.id);
        }
    }, [selectedWerteListe, refetch]);

    useEffect(() => {
        if (data) {
            setFilteredData([
                ...data.filter((item) => {
                    return (
                        !filter ||
                        item?.id?.toString().toLowerCase().includes(filter.toLowerCase()) ||
                        item?.value?.toLowerCase().includes(filter.toLowerCase())
                    );
                }),
            ]);
        }
    }, [data, filter, loading]);

    async function handleDelete(index) {
        if (isDisabled()) {
            return;
        }

        confirm(
            confirmDeleteOption(
                "den Wert",
                () => deleteWert(index),
                () => {
                }
            )
        );
    }

    async function deleteWert(index) {
        await http
            .delete(selectedWerteListe.id + filteredData.find((o) => o.id === index).id)
            .then(() => toast.success("Gelöscht"))
            .catch((e) => {
                if (!isApiCancel(e)) {
                    if (e.response && e.response.status === 409) {
                        toast.error("Der Wertelisteneintrag wird von anderen Objekten referenziert und kann deshalb nicht gelöscht werden");
                    } else {
                        handleApiException(e, "Fehler beim Löschen");
                    }
                }
            });
        setTableIndex(-1);
        refetch(selectedWerteListe.id);
    }

    function handleEdit(index) {
        if (isDisabled()) {
            return;
        }
        setTableIndex(index);
        setDlgOpen(true);
    }

    function save(dataToSave) {
        logger.debug("Saving data", dataToSave);

        setSaving(true);

        const toastSuccess = (refetchId) => {
            toast.success("Gespeichert");
            refetch(refetchId);
            setDlgOpen(false);
            setSaving(false);
        }

        const handleError = (e) => {
            setSaving(false);
            if (e === "validation") return;
            handleApiException(e, "Fehler beim Speichern");
        }

        let dataCopy = {...dataToSave};
        if (dataCopy.id) {
            http.put(selectedWerteListe.id + dataCopy.id, dataCopy)
                .then(() => toastSuccess(selectedWerteListe.id))
                .catch(handleError);
        } else {
            http.post(selectedWerteListe.id, dataCopy)
                .then(() => toastSuccess(selectedWerteListe.id))
                .catch(handleError);
        }
    }

    function isDisabled() {
        return !userHasAnyRole([SYSTEMADMIN]);
    }

    return (
        <div className={"gridl tabsheet-content-margin"}>
            {dlgOpen && (
                <TableDialog
                    open={dlgOpen}
                    saving={saving}
                    data={tableIndex === -1 ? {} : filteredData.find((o) => o.id === tableIndex)}
                    referenzLabel={selectedWerteListe.referenzBezeichnung}
                    referenz={selectedWerteListe.referenz}
                    referenzListe={selectedWerteListe.referenzAbfrage}
                    referenzIsMultiselection={selectedWerteListe.referenzIsMultiselection}
                    onClickOk={(dataToSave) => save(dataToSave)}
                    onClickCancel={() => setDlgOpen(false)}
                    additionalColumns={selectedWerteListe.additionalColumns}
                />
            )}
            <EACombobox
                id={"wertelistenAuswahl"}
                options={wertelistenOptions}
                onChange={(event, newValue) => {
                    setFilter("");
                    setSelectedWerteListe(newValue);
                }}
                value={selectedWerteListe}
                optionKey={"id"}
                optionTitle={"value"}
                label={"Auswahl der Werteliste"}
                style={{paddingBottom: "10px"}}
            />

            <EATextField
                id={"searchField"}
                disabled={!data}
                loading={loading}
                value={filter}
                label={"Suche"}
                onChange={({currentTarget}) => {
                    setFilter(currentTarget.value);
                }}
                style={{paddingBottom: "10px"}}
            />
            <EATable
                id={"wertelist"}
                loading={loading}
                data={filteredData}
                columns={buildColumns(isDisabled(), selectedWerteListe, handleEdit, handleDelete)}
            />
            {data && !isDisabled() && (
                <EAButton
                    id={"addWert"}
                    loading={loading}
                    label={"Hinzufügen"}
                    onClick={() => {
                        setTableIndex(-1);
                        setDlgOpen(true);
                    }}
                    style={{justifySelf: "end"}}
                />
            )}
        </div>
    );
};

export default WertelistenComponent;
